import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED') {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const { data } = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        error.config.headers.Authorization = `Bearer ${data.data.token}`;
        return api(error.config);
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  customerLogin: (data) => api.post('/auth/customer-login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// ─── Customers ─────────────────────────────────────────────────────────────────
export const customerAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  getByStb: (serial) => api.get(`/customers/by-stb/${encodeURIComponent(serial)}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  updateStatus: (id, data) => api.put(`/customers/${id}/status`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getPayments: (id) => api.get(`/customers/${id}/payments`),
  getComplaints: (id) => api.get(`/customers/${id}/complaints`),
  getAreas: () => api.get('/customers/areas'),
  getStats: () => api.get('/customers/stats'),
  // Customer self-service
  getMe: () => api.get('/customers/me'),
  updateMe: (data) => api.put('/customers/me', data),
  requestPlanChange: (data) => api.post('/customers/me/plan-change', data),
  requestDisconnection: (data) => api.post('/customers/me/disconnect-request', data),
};

// ─── Config ────────────────────────────────────────────────────────────────────
export const configAPI = {
  get: () => api.get('/config'),
};

// ─── Broadband Plans ───────────────────────────────────────────────────────────
export const broadbandAPI = {
  getPlans: (params) => api.get('/broadband', { params }),
  createPlan: (data) => api.post('/broadband', data),
  updatePlan: (id, data) => api.put(`/broadband/${id}`, data),
  deletePlan: (id) => api.delete(`/broadband/${id}`),
  assignPlan: (data) => api.post('/broadband/assign', data),
};

// ─── CATV ──────────────────────────────────────────────────────────────────────
export const catvAPI = {
  getPackages: (params) => api.get('/catv', { params }),
  createPackage: (data) => api.post('/catv', data),
  updatePackage: (id, data) => api.put(`/catv/${id}`, data),
  assignPackage: (data) => api.post('/catv/assign', data),
};

// ─── Payments ──────────────────────────────────────────────────────────────────
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  getById: (id) => api.get(`/payments/${id}`),
  getSummary: () => api.get('/payments/summary'),
  getMyToday: () => api.get('/payments/my-today'),
  getReceiptUrl: (id) => `${API_URL}/payments/${id}/receipt`,
};

// ─── Renewals ──────────────────────────────────────────────────────────────────
export const renewalAPI = {
  getUpcoming: (params) => api.get('/renewals/upcoming', { params }),
  getExpired: () => api.get('/renewals/expired'),
  getHistory: (params) => api.get('/renewals/history', { params }),
  getDashboard: () => api.get('/renewals/dashboard'),
};

// ─── Complaints ────────────────────────────────────────────────────────────────
export const complaintAPI = {
  getAll: (params) => api.get('/complaints', { params }),
  getById: (id) => api.get(`/complaints/${id}`),
  create: (data) => api.post('/complaints', data),
  assign: (id, data) => api.put(`/complaints/${id}/assign`, data),
  updateStatus: (id, data) => api.put(`/complaints/${id}/update-status`, data),
  addComment: (id, data) => api.post(`/complaints/${id}/comment`, data),
  getStats: () => api.get('/complaints/stats'),
};

// ─── Devices ───────────────────────────────────────────────────────────────────
export const deviceAPI = {
  getAll: (params) => api.get('/devices', { params }),
  getById: (id) => api.get(`/devices/${id}`),
  create: (data) => api.post('/devices', data),
  update: (id, data) => api.put(`/devices/${id}`, data),
  install: (id, data) => api.post(`/devices/${id}/install`, data),
  getStats: () => api.get('/devices/stats'),
};

// ─── ACS / ONT Control (TR-069) ────────────────────────────────────────────────
export const acsAPI = {
  // Admin
  getStatus: () => api.get('/acs/status'),
  getDevices: (params) => api.get('/acs/devices', { params }),
  getDevice: (serial, raw) => api.get(`/acs/devices/${encodeURIComponent(serial)}`, { params: raw ? { raw: 1 } : {}, timeout: 30000 }),
  changeWifi: (serial, data) => api.post(`/acs/devices/${encodeURIComponent(serial)}/wifi`, data, { timeout: 45000 }),
  reboot: (serial) => api.post(`/acs/devices/${encodeURIComponent(serial)}/reboot`, {}, { timeout: 30000 }),
  factoryReset: (serial) => api.post(`/acs/devices/${encodeURIComponent(serial)}/factory-reset`, {}, { timeout: 30000 }),
  refresh: (serial) => api.post(`/acs/devices/${encodeURIComponent(serial)}/refresh`, {}, { timeout: 60000 }),
  setParams: (serial, values) => api.post(`/acs/devices/${encodeURIComponent(serial)}/params`, { values }, { timeout: 45000 }),
  firmware: (serial, data) => api.post(`/acs/devices/${encodeURIComponent(serial)}/firmware`, data, { timeout: 45000 }),
  ping: (serial, host) => api.post(`/acs/devices/${encodeURIComponent(serial)}/ping`, { host }, { timeout: 45000 }),
  getPingResults: (serial) => api.get(`/acs/devices/${encodeURIComponent(serial)}/ping-results`),
  deleteDevice: (serial) => api.delete(`/acs/devices/${encodeURIComponent(serial)}`),
  // Customer self-service
  getMyOnt: () => api.get('/acs/my-ont', { timeout: 30000 }),
  changeMyWifiPassword: (data) => api.post('/acs/my-ont/wifi-password', data, { timeout: 45000 }),
  rebootMyOnt: () => api.post('/acs/my-ont/reboot', {}, { timeout: 30000 }),
};

// ─── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboardAPI = {
  getOverview: () => api.get('/dashboard/overview'),
  getMonthlyGrowth: () => api.get('/dashboard/monthly-growth'),
  getAreaStats: () => api.get('/dashboard/area-stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
};

// ─── Reports ───────────────────────────────────────────────────────────────────
export const reportAPI = {
  dailyCollection: (params) => api.get('/reports/daily-collection', { params }),
  monthlyCollection: (params) => api.get('/reports/monthly-collection', { params }),
  dueCustomers: () => api.get('/reports/due-customers'),
  areaWise: () => api.get('/reports/area-wise'),
  complaints: (params) => api.get('/reports/complaints', { params }),
  renewal: (params) => api.get('/reports/renewal', { params }),
};

// ─── Users ─────────────────────────────────────────────────────────────────────
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// ─── Money Manager ─────────────────────────────────────────────────────────────
export const moneyAPI = {
  getSummary: (period) => api.get('/money/summary', { params: { period } }),
  getTransactions: (params) => api.get('/money/transactions', { params }),
  createTransaction: (data) => api.post('/money/transactions', data),
  updateTransaction: (id, data) => api.put(`/money/transactions/${id}`, data),
  deleteTransaction: (id) => api.delete(`/money/transactions/${id}`),
};

// ─── Import ────────────────────────────────────────────────────────────────────
export const importAPI = {
  getTemplateUrl:  () => `${API_URL}/import/template`,
  previewFile:     (formData) => api.post('/import/preview-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  previewSheet:    (url) => api.post('/import/preview-sheet', { url }),
  run:             (data) => api.post('/import/run', data),
};

// ─── App Options (addable dropdowns) ──────────────────────────────────────────
export const optionsAPI = {
  get:    (key)        => api.get(`/options/${key}`),
  add:    (key, value) => api.post(`/options/${key}`, { value }),
  remove: (key, value) => api.delete(`/options/${key}/${encodeURIComponent(value)}`),
};

export const kycAPI = {
  sendOTP:       (customerId, mobile) => api.post('/kyc/send-otp', { customerId, mobile }),
  verifyOTP:     (customerId, otp)    => api.post('/kyc/verify-otp', { customerId, otp }),
  complete:      (customerId, data)   => api.put(`/kyc/${customerId}`, data),
  assignDefault: (customerId)         => api.post(`/kyc/assign-default/${customerId}`),
  pending:       ()                   => api.get('/kyc/pending'),
};

export default api;
